import api from "@/api";
import i18n from "@/i18n";

const { t } = i18n.global;

export default {
  state: {
    policyFiles: [],
    policyFileId: "",
  },
  getters: {
    policyFiles: (state) => state.policyFiles,
    policyFileId: (state) => state.policyFileId,
  },
  mutations: {
    setPolicyFiles: (state, data) => {
      state.policyFiles = data;
    },

    setPolicyFileId: (state, data) => {
      state.policyFileId = data;
    },
  },
  actions: {
    async addPolicy({ commit }, {
        customerId,
        companyId,
        agentId,
        agentId2,
        saleCategory,
        policyMandate,
        policyServices,
        submissionDate,
        teamId
      }) {
      try {
        const response = await api({ requiresAuth: true }).post("/v1/policies", {
          customer_id: customerId,
          company_id: companyId,
          agent_id: agentId,
          policy_mandate: policyMandate,
          policy_services: policyServices,
          submit_date: submissionDate,
          team_id: teamId,
          second_agent_id: agentId2,
          second_agent_sales_category_id: saleCategory,
        });
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.policyCreatedSuccessfully")
        }, { root: true });
        return response;
      } catch (error) {
        commit("setToast", {
          display: true,
          type: "error",
          message: error.response?.data?.message || t("messages.failedToCreatePolicy")
        }, { root: true }); 
      }
    },

    updatePolicyStatus: async ({ commit }, { policyId, policyStatusId }) => {
      try {
        await api({ requiresAuth: true }).patch(
          `/v1/policies/${policyId}/statuses`,
          {
            policy_status_id: policyStatusId,
          }
        );
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.policyStatusUpdatedSuccessfully")
        }, { root: true });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.failedToUpdatePolicyStatus")
        }, { root: true }); 
      }
    },

    updatePolicyAgent: async ({ commit }, { policyId, policyAgentId }) => {
      try {
        await api({ requiresAuth: true }).patch(
          `/v1/policies/${policyId}/agents`,
          {
            agent_id: policyAgentId,
          }
        );
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.policyAgentUpdatedSuccessfully")
        }, { root: true });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.failedToUpdatePolicyAgent")
        }, { root: true }); 
      }
    },

    updateSecondPolicyAgent: async ({ commit }, { policyId, secondAgentId, secondAgentSalesCategoryId }) => {
      try {
        await api({ requiresAuth: true }).patch(
          `/v1/policies/${policyId}/second-agents`,
          {
            second_agent_id: secondAgentId,
            second_agent_sales_category_id: secondAgentSalesCategoryId,
          }
        );
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.policySecondAgentUpdatedSuccessfully")
        }, { root: true });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.failedToUpdatePolicySecondAgent")
        }, { root: true });
      }
    },

    deleteSecondPolicyAgent: async ({ commit }, { policyId }) => {
      try {
        await api({ requiresAuth: true }).delete(
          `/v1/policies/${policyId}/second-agents`
        );
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.policySecondAgentDeletedSuccessfully")
        }, { root: true });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.failedToDeletePolicySecondAgent")
        }, { root: true });
      }
    },

    updatePolicyNumber: async ({ commit }, { policyId, policyNumber }) => {
      try {
        await api({ requiresAuth: true }).patch(
          `/v1/policies/${policyId}/policyNumbers`,
          {
            policy_number: policyNumber,
          }
        );
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.policyNumberUpdatedSuccessfully")
        }, { root: true });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.failedToUpdatePolicyNumber")
        }, { root: true }); 
      }
    },

    updatePlateNumber: async ({ commit }, { policyId, plateNumber }) => {
      try {
        await api({ requiresAuth: true }).patch(
          `/v1/policies/${policyId}/plateNumber`,
          {
            plate_number: plateNumber,
          }
        );
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.plateNumberUpdatedSuccessfully")
        }, { root: true });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.failedToUpdatePlateNumber")
        }, { root: true }); 
      }
    },

    updateSubmitDate: async ({ commit }, { policyId, submitDate }) => {
      try {
        await api({ requiresAuth: true }).patch(
          `/v1/policies/${policyId}/submitDate`,
          {
            submit_date: submitDate,
          }
        );
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.submitDateUpdatedSuccessfully")
        }, { root: true });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.failedToUpdateSubmitDate")
        }, { root: true }); 
      }
    },

    async addPolicyFile({ commit }, { policyId, files }) {
      const formData = new FormData();

      (Array.isArray(files) ? files : []).forEach((fileEntry, index) => {
        const file = fileEntry?.file || fileEntry;
        const documentTypeId = fileEntry?.documentTypeId ?? fileEntry?.document_type_id ?? fileEntry?.documentType?.id ?? null;

        if (file) {
          formData.append(`policy_files[${index}][file]`, file);
        }

        if (documentTypeId !== null && documentTypeId !== undefined) {
          formData.append(`policy_files[${index}][document_type]`, String(documentTypeId));
        }
      });

      try {
        const response = await api({ requiresAuth: true }).post(
          `/v1/policies/${policyId}/files`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.policyFilesUploadedSuccessfully")
        }, { root: true });

        return response?.data ?? [];
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.failedToUploadPolicyFiles")
        }, { root: true }); 
        return [];
      }
    },
  },
};
